import { ArrowBack, ArrowForward } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { Show, useIsMobile } from '@side/pantry';
import { useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import {
  CARD_SPACING,
  getCardsPerPage,
  TOTAL_CARD_WIDTH,
} from '@/views/Home/components/NeedsAttention/helpers';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import { CARD_DIMENSION_IN_PIXELS } from './NeedsAttentionCard';

const FullWidthContainer = styled(Box)(({ theme }) => ({
  overflowX: 'hidden',
  width: `calc(100vw - ${theme.spacing(2)}px )`,
  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),
  [theme.breakpoints.up('md')]: {
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
  },
}));

const ScrollableContainer = styled(Stack)(({ theme: { breakpoints } }) => ({
  msOverflowStyle: 'none' /* Hide scrollbar in Edge */,
  scrollbarWidth: 'none' /* Hide scrollbar in Firefox */,
  '&::-webkit-scrollbar': {
    display: 'none' /* Hide scrollbar in Chrome, Safari and Opera */,
  },
  alignItems: 'flex-start',
  display: 'flex',
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  scrollPaddingLeft: 16,
  [breakpoints.up('md')]: {
    overflowX: 'hidden',
  },
}));

interface Props {
  items: React.ReactNode[];
}

export const Carousel = ({ items }: Props) => {
  const trackEvent = useTrackHomePageAnalytics();
  const isMobile = useIsMobile('md');
  const [count, setCount] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(4);
  const carouselRef = useRef<HTMLDivElement>(null);

  const onNextClick = () => {
    setCount((prev) => prev + 1);
    trackEvent(TrackHomePageEvent.NeedsAttentionCarouselPaginationClick, {
      eventTarget: 'next_button',
    });
  };
  const onPreviousClick = () => {
    setCount((prev) => prev - 1);
    trackEvent(TrackHomePageEvent.NeedsAttentionCarouselPaginationClick, {
      eventTarget: 'previous_button',
    });
  };

  useEffectOnce(() => {
    const computeCardsPerPage = () => {
      setCount(0);
      const newCardsPerPage = getCardsPerPage(carouselRef.current);
      if (newCardsPerPage) {
        setCardsPerPage(newCardsPerPage);
      }
    };

    computeCardsPerPage();
    window.addEventListener('resize', computeCardsPerPage);

    return () => window.removeEventListener('resize', computeCardsPerPage);
  });

  return (
    <FullWidthContainer ref={carouselRef}>
      <ScrollableContainer
        sx={{
          width: isMobile
            ? '100vw'
            : CARD_SPACING * (cardsPerPage - 1) +
              CARD_DIMENSION_IN_PIXELS * cardsPerPage,
        }}
      >
        <Stack
          direction="row"
          component="ul"
          sx={{
            gap: `${CARD_SPACING}px`,
            transition: 'transform .5s ease',
            transform: `translateX(-${count * TOTAL_CARD_WIDTH}px)`,
            p: 0,
            mx: { xs: 2, md: 0 },
          }}
        >
          {items}
        </Stack>
      </ScrollableContainer>
      <Show when={!isMobile && items.length > cardsPerPage}>
        <Stack
          direction="row"
          sx={{
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            width:
              CARD_SPACING * (cardsPerPage - 1) +
              CARD_DIMENSION_IN_PIXELS * cardsPerPage,
          }}
        >
          <Button
            variant="text"
            startIcon={<ArrowBack />}
            onClick={onPreviousClick}
            disabled={count === 0}
          >
            Prev
          </Button>
          <Button
            variant="text"
            endIcon={<ArrowForward />}
            onClick={onNextClick}
            disabled={count >= items.length - cardsPerPage}
          >
            Next
          </Button>
        </Stack>
      </Show>
    </FullWidthContainer>
  );
};
